/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { useMemo } from "react";
import { BsFillLightningFill } from "react-icons/bs";
import { useRecoilValue } from "recoil";
import { TempCampaignAtom } from "../../../../atoms/CampaignAtom";
import { CampaingBuilderFormAtom } from "../../../../atoms/CampaingBuilderFormAtom";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import useMediaQuery from "../../../../hook/useMediaQuery";
import { LearnisticAppsToTags } from "../../../../utils/LearnisticTagConvertions";
import { CUSTOM_TAG } from "../../../Contacts/Header";
import LearnisticTag from "../../../LearnisticTag";
import Tag from "../../../Tag";

interface ContainerProps {
  selected: boolean;
  error: boolean;
  isFormOpen?: boolean;
}

const Container = styled(motion.div)<ContainerProps>`
  width: ${(props) => (props.isFormOpen ? "82px" : "240px")};
  min-height: ${(props) => (props.isFormOpen ? "82px" : "fit-content")};
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #dddddd;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => (props.isFormOpen ? "center" : "start")};
  cursor: pointer;

  .tags-wrapper {
    width: 100%;
    height: fit-content;

    padding-bottom: 2rem;

    .tag-item {
      display: flex;
      align-items: center;
      justify-content: start;
      flex-wrap: wrap;
      grid-gap: 12px;

      > div {
        margin: 0;
      }
    }
  }

  svg {
    width: ${(props) => (props.isFormOpen ? "40px" : "16px")};
    height: ${(props) => (props.isFormOpen ? "40px" : "16px")};
  }

  ${(props) =>
    props.selected &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}
  ${(props) =>
    props.error &&
    css`
      box-shadow: 0px 0px 6px #ff0000;
      border: 1px solid #ff0000;
    `}


    @media screen and (min-width: 1080px) {
    width: 240px;
    min-height: fit-content;
    justify-content: start;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-size: 16px;
  font-weight: 600;

  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    margin-right: 4px;
  }
`;

const TagNameWrapper = styled.div`
  width: calc(100% + 32px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  grid-gap: 8px;
  margin-top: 8px;
  margin-bottom: -16px;
  padding-bottom: 8px;
`;

const Tags = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: -10px;
`;

interface InitialCard {
  onClick: () => any;
  selected: boolean;
  error: boolean;
}

export default function InitialCard({ onClick, selected, error }: InitialCard) {
  const form = useRecoilValue(CampaingBuilderFormAtom);
  const tempCampaign = useRecoilValue(TempCampaignAtom);
  const tags = useRecoilValue(TagsAtom);
  const isLargerThan1080 = useMediaQuery("(min-width: 1080px)");

  const tagsToShow = useMemo(() => {
    if (!tempCampaign || !tempCampaign.tagConditions.length) return;

    const startTagConditions: any[] = [
      ...// Get all tags that are not Learnistic
      (tempCampaign.tagConditions.filter((tg) => !tg.learnisticTag) || [])
        // Map to their BM counterparts
        .map((tg) => tags.find((tag) => tag._id === tg.tagId)),

      // Use this shitty named function to map the other ones
      ...LearnisticAppsToTags(
        (tempCampaign.tagConditions || [])
          .filter((n) => n && n.learnisticTag)
          .map((tg) => tg.learnisticTag) || []
      ),
    ];

    if (startTagConditions.length < 3) {
      return (
        <Tags className="tag-item">
          {startTagConditions.map((elem) =>
            elem?._id ? (
              <Tag key={`flex_tags_any_${elem?._id}`} tag={elem as any} />
            ) : (
              <LearnisticTag
                key={`recipient_tag_tags_builder_${elem?.id}`}
                tag={elem}
              />
            )
          )}
        </Tags>
      );
    } else {
      const [explicitTag, explicitTag2, ...bundledTags] = startTagConditions;
      const explicitTags = [explicitTag, explicitTag2];
      const system = !bundledTags.find(
        (bTag) => bTag?.type === "user" || bTag?.id
      );

      return (
        <Tags className="tag-item">
          {explicitTags
            .filter((t) => t)
            .map((et) =>
              et?._id ? (
                <Tag key={`flex_tags_any_${et._id}`} tag={et as any} />
              ) : (
                <LearnisticTag
                  key={`recipient_tag_tags_builder_${et?.id}`}
                  tag={et}
                />
              )
            )}
          <div data-tip data-for="tag">
            <Tag
              tag={CUSTOM_TAG(startTagConditions.length - 2, system)}
              tooltip={bundledTags
                .filter((t) => t)
                .map(
                  // If the tag doesn't have a _id, then it's from Learnistic
                  ({ category, name, _id: berserkerMailId }) =>
                    `${berserkerMailId ? category : "LEARNISTIC"} -> ${name}`
                )
                .join("\n")}
            />
          </div>
        </Tags>
      );
    }
  }, [tags, tempCampaign]);

  return (
    <Container
      isFormOpen={form !== null}
      onClick={onClick}
      selected={selected}
      error={error}
    >
      {isLargerThan1080 || form === null ? (
        <>
          <Title style={{ textTransform: "uppercase" }}>
            <BsFillLightningFill color="#FB972E" />
            Start
          </Title>
          <TagNameWrapper className="tags-wrapper">{tagsToShow}</TagNameWrapper>
        </>
      ) : (
        <BsFillLightningFill color="#FB972E" />
      )}
    </Container>
  );
}
